import { useEffect } from "react";
import { Card, CardBody, Spinner, Chip, Avatar, Button, Divider } from "@heroui/react";
import {
  MessageCircle,
  Heart,
  Repeat2,
  TrendingUp,
  TrendingDown,
  Zap,
  RefreshCw,
  ExternalLink,
  CheckCircle,
  Shield,
  Award,
  Target,
  Activity,
} from "lucide-react";
import { useLeaderboardStore } from "@/stores/leaderboardStore";
import { formatNumber } from "@/lib/utils";
import type { AlphaTweet, UserTrackRecord, PerformanceTier } from "@/types";
import { formatDistanceToNow } from "date-fns";

function SentimentBadge({ sentiment }: { sentiment: number }) {
  const isBullish = sentiment > 5;
  const Icon = isBullish ? TrendingUp : TrendingDown;
  const color = isBullish ? "success" : "danger";
  const label = isBullish ? "Bullish" : "Bearish";

  return (
    <Chip size="sm" color={color} variant="flat" startContent={<Icon className="w-3 h-3" />}>
      {label} ({sentiment}/10)
    </Chip>
  );
}

function AlphaScoreBadge({ score }: { score: number }) {
  const color = score >= 8 ? "warning" : score >= 6 ? "primary" : "default";
  return (
    <Chip size="sm" color={color} variant="bordered" startContent={<Zap className="w-3 h-3" />}>
      Alpha: {score}/10
    </Chip>
  );
}

function TierBadge({ tier }: { tier: PerformanceTier }) {
  const config = {
    top_performer: { color: "success" as const, label: "Top Performer", icon: Award },
    proven: { color: "primary" as const, label: "Proven", icon: Target },
    unproven: { color: "default" as const, label: "Unproven", icon: Activity },
  };
  const { color, label, icon: Icon } = config[tier];

  return (
    <Chip size="sm" color={color} variant="dot" startContent={<Icon className="w-3 h-3" />}>
      {label}
    </Chip>
  );
}

function TrackRecordBadge({ trackRecord }: { trackRecord: UserTrackRecord | null }) {
  if (!trackRecord || trackRecord.signalsWith24hOutcome < 5) return null;

  const accuracy = trackRecord.accuracy24h;
  if (accuracy === null) return null;

  return (
    <div className="flex items-center gap-2">
      <TierBadge tier={trackRecord.performanceTier} />
      <Chip size="sm" variant="flat">
        {accuracy.toFixed(0)}% acc ({trackRecord.signalsWith24hOutcome} signals)
      </Chip>
    </div>
  );
}

function LegitimacyBadge({ value }: { value: number }) {
  if (value < 7) return null;
  return (
    <Chip size="sm" color="success" variant="dot" startContent={<Shield className="w-3 h-3" />}>
      Verified ({value}/10)
    </Chip>
  );
}

function TweetCard({
  tweet,
  trackRecord,
}: {
  tweet: AlphaTweet;
  trackRecord: UserTrackRecord | null;
}) {
  const timeAgo = formatDistanceToNow(new Date(tweet.createdAtTimestamp * 1000), {
    addSuffix: true,
  });

  const isTopPerformer = trackRecord?.performanceTier === "top_performer";
  const isProven = trackRecord?.performanceTier === "proven";

  return (
    <Card className={`mb-4 ${isTopPerformer ? "border-2 border-success/30" : isProven ? "border border-primary/20" : ""}`}>
      <CardBody className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Avatar
                name={tweet.twitterHandle.charAt(0).toUpperCase()}
                size="md"
                className={`${
                  isTopPerformer
                    ? "bg-success/20 text-success"
                    : isProven
                    ? "bg-primary/20 text-primary"
                    : "bg-default-100"
                }`}
              />
              {isTopPerformer && (
                <Award className="w-4 h-4 text-success absolute -top-1 -right-1" />
              )}
            </div>
            <div>
              <a
                href={`https://twitter.com/${tweet.twitterHandle}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold hover:text-primary flex items-center gap-1"
              >
                @{tweet.twitterHandle}
                <ExternalLink className="w-3 h-3" />
              </a>
              <div className="text-xs text-default-400">{timeAgo}</div>
            </div>
          </div>
          <Chip size="sm" variant="flat" color="secondary">
            ${tweet.tokenSymbol}
          </Chip>
        </div>

        {/* Track Record Banner */}
        {trackRecord && trackRecord.signalsWith24hOutcome >= 5 && (
          <div className="mb-3 p-2 bg-default-50 rounded-lg">
            <div className="flex items-center justify-between text-xs">
              <TrackRecordBadge trackRecord={trackRecord} />
              <span className="text-default-400">
                Avg return: {trackRecord.avgReturn24h !== null ? `${trackRecord.avgReturn24h >= 0 ? "+" : ""}${trackRecord.avgReturn24h.toFixed(1)}%` : "N/A"}
              </span>
            </div>
          </div>
        )}

        {/* Tweet text */}
        <p className="text-sm mb-3 whitespace-pre-wrap line-clamp-6">{tweet.text}</p>

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mb-3">
          <SentimentBadge sentiment={tweet.sentiment} />
          <AlphaScoreBadge score={tweet.alpha} />
          <LegitimacyBadge value={tweet.legitimacy} />
          {tweet.isRecent && (
            <Chip size="sm" variant="flat" color="primary">
              Recent
            </Chip>
          )}
          {tweet.isVerifiable && (
            <Chip size="sm" variant="flat" startContent={<CheckCircle className="w-3 h-3" />}>
              Verifiable
            </Chip>
          )}
        </div>

        {/* Reasoning (collapsed by default) */}
        {tweet.scoresReasoning && (
          <details className="mb-3">
            <summary className="text-xs text-default-400 cursor-pointer hover:text-default-500">
              View scoring reasoning
            </summary>
            <p className="text-xs text-default-500 mt-2 p-2 bg-default-50 rounded">
              {tweet.scoresReasoning}
            </p>
          </details>
        )}

        <Divider className="my-3" />

        {/* Engagement metrics */}
        <div className="flex items-center gap-4 text-default-400 text-sm">
          <div className="flex items-center gap-1">
            <Heart className="w-4 h-4" />
            {formatNumber(tweet.favoriteCountAtCollection)}
          </div>
          <div className="flex items-center gap-1">
            <Repeat2 className="w-4 h-4" />
            {formatNumber(tweet.retweetCountAtCollection)}
          </div>
          <div className="flex items-center gap-1">
            <MessageCircle className="w-4 h-4" />
            {formatNumber(tweet.replyCountAtCollection)}
          </div>
          <a
            href={tweet.tweetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-primary hover:underline flex items-center gap-1"
          >
            View on X <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </CardBody>
    </Card>
  );
}

export function SocialFeed() {
  const {
    tweets,
    feedLoading,
    feedError,
    minAlpha,
    setMinAlpha,
    loadFeed,
    loadLeaderboard,
    getTrackRecord,
    trackRecords,
  } = useLeaderboardStore();

  useEffect(() => {
    loadFeed();
    // Also load leaderboard to get track records
    if (trackRecords.length === 0) {
      loadLeaderboard();
    }
  }, []);

  const handleRefresh = () => {
    loadFeed();
  };

  const handleAlphaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setMinAlpha(parseInt(e.target.value));
    loadFeed();
  };

  // Count tweets from proven/top performers
  const topPerformerTweets = tweets.filter((t) => {
    const tr = getTrackRecord(t.twitterHandle);
    return tr?.performanceTier === "top_performer";
  }).length;

  const provenTweets = tweets.filter((t) => {
    const tr = getTrackRecord(t.twitterHandle);
    return tr?.performanceTier === "proven";
  }).length;

  return (
    <div className="w-full">
      <Card className="mb-4">
        <CardBody className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Zap className="w-6 h-6 text-warning" />
              <div>
                <h2 className="text-xl font-bold">Alpha Feed</h2>
                <p className="text-sm text-default-500">
                  High-signal tweets with analyst track records
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="flat"
              startContent={<RefreshCw className="w-4 h-4" />}
              onPress={handleRefresh}
              isLoading={feedLoading}
            >
              Refresh
            </Button>
          </div>

          {/* Stats */}
          {tweets.length > 0 && (
            <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-default-50 rounded-lg text-sm">
              <div className="text-center">
                <div className="font-bold text-success">{topPerformerTweets}</div>
                <div className="text-xs text-default-400">From Top Performers</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-primary">{provenTweets}</div>
                <div className="text-xs text-default-400">From Proven</div>
              </div>
              <div className="text-center">
                <div className="font-bold">{tweets.length}</div>
                <div className="text-xs text-default-400">Total Tweets</div>
              </div>
            </div>
          )}

          {/* Filter */}
          <div className="p-4 bg-default-50 rounded-lg">
            <label className="text-sm font-medium mb-2 block">Min Alpha Score</label>
            <select
              value={minAlpha}
              onChange={handleAlphaChange}
              className="w-full max-w-xs px-3 py-2 rounded-lg border border-default-200 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((val) => (
                <option key={val} value={val}>
                  {val}/10
                </option>
              ))}
            </select>
          </div>
        </CardBody>
      </Card>

      {feedError && (
        <div className="p-4 mb-4 bg-danger-50 text-danger rounded-lg">{feedError}</div>
      )}

      {feedLoading && tweets.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          {tweets.map((tweet) => (
            <TweetCard
              key={tweet.tweetUrl}
              tweet={tweet}
              trackRecord={getTrackRecord(tweet.twitterHandle)}
            />
          ))}

          {tweets.length > 0 && (
            <div className="text-sm text-default-400 text-center py-4">
              Showing {tweets.length} alpha tweets
            </div>
          )}

          {tweets.length === 0 && !feedLoading && (
            <Card>
              <CardBody className="p-8 text-center text-default-400">
                No alpha tweets found matching criteria
              </CardBody>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
